/**
 * upsertMissionPoint — shared upsert for Mini-Project mission_metric_points
 * (DEV-1075 Phase 7.3a-ii).
 *
 * Mirrors the FP MissionDataEntry pattern:
 *   1. POST a new point.
 *   2. If 409 Conflict (existing point for the natural key), fetch the
 *      matching point and PUT to update its value.
 *
 * Natural key for non-checklist edits:
 *   (mini_project_guid, sub_kpi_key, reporting_date, item_id IS NULL)
 *
 * Natural key for checklist item edits:
 *   (mini_project_guid, sub_kpi_key, item_id). Note that reporting_date
 *   is irrelevant for the checklist dedup — re-completing the same
 *   item updates the existing event.
 *
 * Used by:
 *   - MiniProjectGoalCard (inline checklist toggle)
 *   - GoalDataEntryTray (numeric value entry for count / percentage /
 *     currency primitives)
 *
 * Extracted from MiniProjectGoalCard into this shared module so both
 * call sites share the same retry semantics without duplicating the
 * 409→PUT branch logic.
 */

import {
  createMissionMetricPoint,
  updateMissionMetricPoint,
  fetchMissionMetricPointsByMiniProject,
} from "../../api/missionMetrics";
import { ApiError } from "../../api/client";

/** YYYY-MM-DD for today, local time. The unified reporting_date column
 *  is a DATE not a TIMESTAMP — timezone considerations only matter at
 *  midnight boundaries, which is acceptable behavior for goal edits
 *  (the Lead's clock decides what "today" means). */
export function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export async function upsertMissionPoint(args: {
  miniProjectGuid: string;
  subKpiKey: string;
  reportingDate: string;
  value: number;
  itemId?: string;
  note?: string;
}): Promise<void> {
  try {
    await createMissionMetricPoint({
      miniProjectGuid: args.miniProjectGuid,
      subKpiKey: args.subKpiKey,
      reportingDate: args.reportingDate,
      value: args.value,
      itemId: args.itemId,
      note: args.note,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const existing = await fetchMissionMetricPointsByMiniProject(
        args.miniProjectGuid,
        args.subKpiKey,
      );
      const match = existing.find((p) =>
        args.itemId
          ? p.itemId === args.itemId
          : p.reportingDate === args.reportingDate && !p.itemId,
      );
      if (match) {
        await updateMissionMetricPoint(match.id, {
          value: args.value,
          note: args.note,
        });
      } else {
        throw err;
      }
    } else {
      throw err;
    }
  }
}
